<template>
  <section v-if="fields && items.length" :class="rootClass" :style="rootStyle">
    <ContentGrid :desktop-default-grid="[1, 22]" :mobile-default-grid="[1, 14]">
      <div class="c-location-grid__inner">
        <article v-for="item in items" :key="item.id" class="c-location-grid__card">
          <Icon class="c-location-grid__icon" name="location" />
          <RichText
            v-if="item.fields.title?.value"
            class="c-location-grid__title"
            :field="item.fields.title"
          />
          <RichText
            v-if="item.fields.description?.value"
            class="c-location-grid__description"
            :field="item.fields.description"
          />
        </article>
      </div>
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RichText, Text as JssText } from '@sitecore-jss/sitecore-jss-vue';
import type { LocationGridItemFields, LocationGridProps } from '@/types/components/LocationGrid';
import type { ScItem } from '@/types/sitecore';
import utils from '@/utils';

interface NormalizedItem extends ScItem<LocationGridItemFields> {
  fields: LocationGridItemFields;
}

const props = defineProps<LocationGridProps>();
const fields = computed(() => props.fields);

const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'c-location-grid'));
const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});

const rawItems = computed<ScItem<LocationGridItemFields>[]>(() => {
  const locationItems = utils.sc.getFieldObj(fields.value, 'items');
  return Array.isArray(locationItems) ? locationItems : [];
});

const items = computed<NormalizedItem[]>(() =>
  rawItems.value.filter((item): item is NormalizedItem => Boolean(item.fields))
);
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-location-grid {
  padding: 20px 0;
  overflow: hidden;

  &__inner {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 32px 30px;
    max-width: 1345px;
    margin: 0 auto;
  }

  &__card {
    padding: 20px 24px;
    border: 1px solid rgba(255, 255, 255, 0.92);
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.5);
    box-shadow: 0 12px 34px rgba(91, 112, 101, 0.08);
    text-align: left;
  }

  &__icon {
    width: 26px;
    color: #6f9874;
    fill: none;
  }

  &__title {
    margin: 12px 0 0;
    color: #3f3f3f;
    font-size: 24px;
    line-height: 1.1;
    font-weight: 400;
    letter-spacing: 0;
  }

  &__description {
    margin-top: 10px;
    color: #7d7d7d;
    font-size: 18px;
    line-height: 1.05;
    font-weight: 400;
    letter-spacing: 0;

    p {
      margin: 0;
    }
  }

  @include tablet {
    padding: 40px 0;

    &__inner {
      gap: 32px;
    }

    &__icon {
      width: 26px;
    }

    &__title {
      font-size: 24px;
    }

    &__description {
      font-size: 18px;
    }
  }

  @include tablet-landscape {
    padding: 40px 0;

    &__inner {
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 31px;
    }

    &__icon {
      width: 26px;
    }

    &__title {
      font-size: 24px;
    }

    &__description {
      font-size: 18px;
      line-height: 1.05;
    }
  }
}
</style>
